import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import { trackedFunction } from 'reactiveweb/function';
import { query } from '@warp-drive/utilities/json-api';
import { PhPlus, PhTrash, PhFile, PhXCircle } from 'ember-phosphor-icons';

import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import FileUploader from 'tio-ui/components/file-uploader';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import Table from 'tio-ui/components/table';
import { divide } from 'tio-ui/math';

import inputmask from 'tio-common/modifiers/inputmask';
import { getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import { humanize } from 'tio-common/helpers/humanize';

import { bulkCreateAccountTransactionsRequest } from 'tio-data/requests/account-transaction';

import type { Store as StoreService } from '@warp-drive/core';
import type ObservablesService from 'tio-common/services/observables';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { AccountTransactionExtension as AccountTransactionModel } from 'tio-data/data/resources/account-transaction.ext';
import type { Enum as EnumModel } from 'tio-data/data/resources/enum.type';
import type { UploadFile } from 'ember-file-upload';

const DEFAULT_TRANSACTION_TYPE = 'TransactionType.PAYMENT';
// Supporting evidence for a manually-entered transaction: a statement PDF, or a
// screenshot (PNG/JPEG) of one — e.g. what a user emails in. Matches the backend
// allowlist for PROXY/PERSON-classified assets (tio-api ObservableAsset).
const ACCEPTED_FILE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'];

const formatEnums = (enums: EnumModel[]): { id: string; name: string }[] =>
  enums.map(({ dot, name }) => ({ id: dot, name: humanize(name) }));

interface TransactionRow {
  amount?: number;
  transactionDate?: string;
  transactionType: string;
}

interface CreateFromDocumentSignature {
  Args: {
    // The person the transactions belong to.
    person: PersonModel;
    // Optional account to associate with each transaction.
    account?: AccountModel;
    // How the supporting document entered the system: 'SELF_REPORTED' (employee
    // self-entry, backend sets provider PERSON) or 'PROXY' (admin on behalf).
    via: 'PROXY' | 'SELF_REPORTED';
    // Called after the transactions are successfully created.
    onComplete?: () => void;
    // When provided, renders a Cancel button beside Submit.
    onCancel?: () => void;
    // Allow choosing each transaction's type (admin). Employees always create
    // payments. Category is always derived from the type on the server, so it is
    // never selected here.
    allowTransactionTypeSelect?: boolean;
    // Extra gate the caller controls (e.g. an employee certification checkbox).
    submitDisabled?: boolean;
    // Pre-selected financial institution (e.g. the admin's account servicer).
    initialFinancialInstitutionId?: string | null;
  };
  Blocks: {
    // Optional financial-institution control (e.g. admin ServicerSelect).
    financialInstitution: [{ selectedKey?: string; onChange: (id: string) => void }];
    // Optional content rendered above the action buttons (e.g. certification).
    beforeActions: [];
  };
  Element: HTMLDivElement;
}

export default class AccountTransactionsCreateFromDocument extends Component<CreateFromDocumentSignature> {
  @service declare store: StoreService;
  @service declare observables: ObservablesService;

  @tracked rows: TransactionRow[] = [
    { amount: undefined, transactionDate: undefined, transactionType: DEFAULT_TRANSACTION_TYPE },
  ];
  @tracked upload?: UploadFile;
  @tracked financialInstitutionId?: string;
  @tracked createdTransactions: AccountTransactionModel[] = [];
  @tracked errorMessage?: string;
  @tracked fileError?: string;

  constructor(owner: Owner, args: CreateFromDocumentSignature['Args']) {
    super(owner, args);
    this.financialInstitutionId = args.initialFinancialInstitutionId ?? undefined;
  }

  fetchTypes = trackedFunction(this, async (): Promise<EnumModel[]> => {
    const { content } = await this.store.request(
      query<EnumModel>('enum', { 'filter[flavor]': 'TransactionType' })
    );
    return (content.data ?? []) as EnumModel[];
  });

  get types(): { id: string; name: string }[] {
    return formatEnums(this.fetchTypes.value ?? []);
  }

  get hasResults(): boolean {
    return this.createdTransactions.length > 0;
  }

  // Admin scope (PROXY) auto-verifies on create; personal scope (SELF_REPORTED)
  // stays pending. Drive the success copy off the actual returned state so admin
  // doesn't see "submitted for verification" for already-verified transactions.
  get allVerified(): boolean {
    return (
      this.createdTransactions.length > 0 &&
      this.createdTransactions.every((txn) => !txn.verificationState?.includes('PENDING'))
    );
  }

  get canRemoveRows(): boolean {
    return this.rows.length > 1;
  }

  get rowsComplete(): boolean {
    return this.rows.every((row) => Boolean(row.amount) && Boolean(row.transactionDate));
  }

  get isSubmitDisabled(): boolean {
    return (
      Boolean(this.args.submitDisabled) ||
      !this.upload ||
      !this.rowsComplete ||
      this.submitTask.isRunning
    );
  }

  setFinancialInstitution = (id: string): void => {
    this.financialInstitutionId = id;
  };

  addRow = (): void => {
    this.rows = [
      ...this.rows,
      { amount: undefined, transactionDate: undefined, transactionType: DEFAULT_TRANSACTION_TYPE },
    ];
  };

  removeRow = (index: number): void => {
    this.rows = this.rows.filter((_, idx) => idx !== index);
  };

  setRowAmount = (index: number, _value: string | number, event: Event): void => {
    const unmasked = getInputmaskUnmaskedValue(event);
    const rows = [...this.rows];
    const row = rows[index];
    if (!row) return;
    row.amount = Math.round(Number(unmasked) * 100);
    this.rows = rows;
  };

  setRowDate = (index: number, value: string): void => {
    const rows = [...this.rows];
    const row = rows[index];
    if (!row) return;
    row.transactionDate = value;
    this.rows = rows;
  };

  setRowType = (index: number, value: string): void => {
    const rows = [...this.rows];
    const row = rows[index];
    if (!row) return;
    row.transactionType = value;
    this.rows = rows;
  };

  // Supporting evidence must be a PDF or a PNG/JPEG screenshot. `@accept` only
  // hints the native file picker, so reject other types explicitly (drag-and-
  // drop bypasses accept) and drop the rejected file from the upload queue.
  handleFileAdded = (upload: UploadFile): void => {
    this.fileError = undefined;
    const extension = upload.file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
      this.fileError = 'invalid_file_type';
      upload.queue?.remove(upload);
      return;
    }
    this.upload = upload;
  };

  removeFile = (): void => {
    const upload = this.upload;
    if (!upload) return;
    upload.queue?.remove(upload);
    this.upload = undefined;
  };

  submitTask = dropTask(async () => {
    this.errorMessage = undefined;

    const { person, account, via } = this.args;
    const upload = this.upload;
    if (!upload) return;

    try {
      const document = await this.observables.createObservableDocument(upload, { person, via });
      if (!document.id || !person.id) return;

      const payload = {
        observableDocumentId: document.id,
        personId: person.id,
        accountId: account?.id ?? undefined,
        financialInstitutionId: this.financialInstitutionId,
        // Category is intentionally omitted — the API derives it from the type.
        transactions: this.rows.map((row) => ({
          amount: row.amount as number,
          transactionDate: row.transactionDate as string,
          transactionType: row.transactionType,
        })),
      };

      const { content } = (await this.store.request(
        bulkCreateAccountTransactionsRequest(payload)
      )) as { content: { data: AccountTransactionModel[] } };
      this.createdTransactions = content.data ?? [];
    } catch {
      this.errorMessage = 'submit_error';
    }
  });

  submit = (event: Event): void => {
    event.preventDefault();
    this.submitTask.perform();
  };

  finish = (): void => {
    this.args.onComplete?.();
  };

  cancel = (): void => {
    this.args.onCancel?.();
  };

  // Friendly label for a dotted enum value, e.g. "TransactionType.PAYMENT" -> "Payment",
  // "TransactionVerificationState.PENDING_VERIFICATION" -> "Pending Verification".
  humanizeEnum = (value?: string): string =>
    value ? humanize(value.split('.').pop() ?? value) : '';

  <template>
    <div ...attributes>
      {{#if this.hasResults}}
        <AlertBanner @intent="success" class="mb-4">
          {{#if this.allVerified}}
            {{t
              "common.account_transactions.create_from_document.created_verified"
              count=this.createdTransactions.length
            }}
          {{else}}
            {{t
              "common.account_transactions.create_from_document.created_success"
              count=this.createdTransactions.length
            }}
          {{/if}}
        </AlertBanner>
        <Table @surface={{true}}>
          <:thead as |options|>
            <options.tr>
              <options.th>{{t "common.account_transactions.new.transaction_date"}}</options.th>
              <options.th>{{t "common.account_transactions.new.amount"}}</options.th>
              <options.th>{{t "common.account_transactions.new.transaction_type"}}</options.th>
              <options.th>{{t
                  "common.account_transactions.create_from_document.status"
                }}</options.th>
            </options.tr>
          </:thead>
          <:tbody as |options|>
            {{#each this.createdTransactions as |transaction|}}
              <options.tr>
                <options.td>{{transaction.transactionDate}}</options.td>
                <options.td>
                  {{safeFormatNumber
                    (divide transaction.amount 100)
                    style="currency"
                    currency="USD"
                  }}
                </options.td>
                <options.td>{{this.humanizeEnum transaction.transactionType}}</options.td>
                <options.td>{{this.humanizeEnum transaction.verificationState}}</options.td>
              </options.tr>
            {{/each}}
          </:tbody>
        </Table>
        <Button @intent="primary" class="mt-4 *:justify-center w-full" {{on "click" this.finish}}>
          {{t "common.account_transactions.create_from_document.done"}}
        </Button>
      {{else}}
        {{#if this.errorMessage}}
          <AlertBanner @intent="danger" class="mb-4">
            {{t "common.account_transactions.create_from_document.submit_error"}}
          </AlertBanner>
        {{/if}}
        <form {{on "submit" this.submit}} class="tio-col-loose">
          <h3 class="tio-text-lg-semibold tio-fg-secondary my-0">
            {{t "common.account_transactions.create_from_document.payments_header"}}
          </h3>
          {{#each this.rows as |row index|}}
            <div class="tio-cluster-tight">
              <Input
                data-test-create-from-document-amount={{index}}
                @label={{t "common.account_transactions.new.amount"}}
                @value={{row.amount}}
                @onChange={{fn this.setRowAmount index}}
                autocomplete="off"
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
              <Input
                data-test-create-from-document-date={{index}}
                @type="date"
                @label={{t "common.account_transactions.new.transaction_date"}}
                @value={{row.transactionDate}}
                @onChange={{fn this.setRowDate index}}
              />
              {{#if @allowTransactionTypeSelect}}
                <Select
                  data-test-create-from-document-type={{index}}
                  @label={{t "common.account_transactions.new.transaction_type"}}
                  @items={{this.types}}
                  @selected={{row.transactionType}}
                  @onChange={{fn this.setRowType index}}
                />
              {{/if}}
              {{#if this.canRemoveRows}}
                <Button
                  data-test-create-from-document-remove-row={{index}}
                  @appearance="outlined"
                  @intent="danger"
                  type="button"
                  aria-label={{t "common.remove"}}
                  {{on "click" (fn this.removeRow index)}}
                >
                  <PhTrash @size="16" />
                </Button>
              {{/if}}
            </div>
          {{/each}}
          <Button
            data-test-create-from-document-add-row
            @appearance="outlined"
            @intent="primary"
            @icon={{PhPlus}}
            @iconPlacement="start"
            type="button"
            {{on "click" this.addRow}}
          >
            {{t "common.account_transactions.create_from_document.add_row"}}
          </Button>

          {{yield
            (hash selectedKey=this.financialInstitutionId onChange=this.setFinancialInstitution)
            to="financialInstitution"
          }}

          <h3 class="tio-text-lg-semibold tio-fg-secondary my-0">
            {{t "common.account_transactions.create_from_document.document_header"}}
          </h3>
          {{#if this.upload}}
            <div class="tio-row-flush tio-fg-muted">
              <PhFile @size="24" />
              <span>{{this.upload.file.name}}</span>
              <button
                class="ml-2"
                type="button"
                aria-label={{t "common.remove"}}
                {{on "click" this.removeFile}}
              >
                <PhXCircle @size="24" />
              </button>
            </div>
          {{else}}
            <FileUploader
              @name="observable-assets"
              @accept=".pdf,.png,.jpg,.jpeg"
              @onFileAdded={{this.handleFileAdded}}
              @useDropzone={{true}}
            />
          {{/if}}
          {{#if this.fileError}}
            <AlertBanner @intent="danger">
              {{t "common.account_transactions.create_from_document.invalid_file_type"}}
            </AlertBanner>
          {{/if}}

          {{yield to="beforeActions"}}

          <div class="tio-row gap-2 mt-4">
            {{#if @onCancel}}
              <Button
                @appearance="outlined"
                type="button"
                class="flex-1 *:justify-center"
                {{on "click" this.cancel}}
              >
                {{t "common.account_transactions.create_from_document.cancel"}}
              </Button>
            {{/if}}
            <Button
              data-test-create-from-document-submit
              @intent="primary"
              type="submit"
              class="flex-1 *:justify-center"
              @isDisabled={{this.isSubmitDisabled}}
            >
              {{t "common.account_transactions.create_from_document.submit"}}
            </Button>
          </div>
        </form>
      {{/if}}
    </div>
  </template>
}
